// src/components/AiRender.jsx
import React, { useState, useEffect } from 'react';
import { functions, auth, db } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc, updateDoc, arrayUnion } from 'firebase/firestore';

const GOLD = '#B8860B';

const AiRender = ({ stylistId = null }) => {
  const [loading, setLoading] = useState(false);
  const [resultImage, setResultImage] = useState(null);
  const [selectedStyle, setSelectedStyle] = useState("low taper fade");
  const [hasBiometricScan, setHasBiometricScan] = useState(false);
  const [scanData, setScanData] = useState(null);

  useEffect(() => {
    const checkBiometrics = async () => {
      if (!auth.currentUser) return;
      const userDoc = await getDoc(doc(db, "users", auth.currentUser.uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        setHasBiometricScan(!!data.hasCompletedScan);
        setScanData(data.lastScanUrl); // Store the actual 3D asset URL
      }
    };
    checkBiometrics();
  }, []);

  const handleHairRender = async () => {
    if (!hasBiometricScan) return alert("3D Cranial Scan required for AI projection.");

    setLoading(true);
    try {
      const renderHairFunction = httpsCallable(functions, 'renderHair');
      
      const response = await renderHairFunction({
        image: scanData, // The 3D Solid Base
        style: selectedStyle,
        userId: auth.currentUser.uid,
        isCranialSolid: true,
        fidelityMode: 'high-res'
      });

      if (response.data.success) {
        setResultImage(response.data.url);
      }
    } catch (error) {
      console.error("ESDRAS AI Engine Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const shareWithStylist = async () => {
    if (!resultImage || !stylistId) return;
    try {
      // Logic to push this projection to the stylist's view/in-box
      await updateDoc(doc(db, "consultations", `${auth.currentUser.uid}_${stylistId}`), {
        projections: arrayUnion({
          imageUrl: resultImage,
          style: selectedStyle,
          timestamp: new Date()
        })
      });
      alert("Projection sent to Stylist's 3D dashboard.");
    } catch (e) {
      console.error("Share failed", e);
    }
  };

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <div>
          <h2 style={{ color: GOLD, margin: 0, fontSize: '1.1rem' }}>3D STYLE PROJECTION</h2>
          <p style={subTextStyle}>Mapped to your unique cranial geometry.</p>
        </div>
        <div style={statusBadge(hasBiometricScan)}>
          {hasBiometricScan ? "BASE READY" : "SCAN MISSING"}
        </div>
      </div>

      

      <div style={controlGroup}>
        <select 
          value={selectedStyle} 
          onChange={(e) => setSelectedStyle(e.target.value)}
          style={selectStyle}
        >
          <option value="low taper fade">Low Taper Fade</option>
          <option value="buzz cut">Buzz Cut</option>
          <option value="waves">360 Waves</option>
          <option value="dreadlock mohawk">Dreadlock Mohawk</option>
        </select>

        <button 
          onClick={handleHairRender}
          disabled={loading || !hasBiometricScan}
          style={btnStyle(loading || !hasBiometricScan)}
        >
          {loading ? "PROCESSING SOLID..." : "GENERATE LOOK"}
        </button>
      </div>

      {resultImage && (
        <div style={resultContainer}>
          <img src={resultImage} alt="AI Result" style={imgStyle} />
          {stylistId && (
            <button onClick={shareWithStylist} style={shareBtn}>
              SHARE WITH STYLIST
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// --- STYLES ---
const containerStyle = { padding: '20px', backgroundColor: '#050a0f', color: 'white', borderRadius: '12px', border: '1px solid #B8860B33' };
const headerStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' };
const subTextStyle = { opacity: 0.5, fontSize: '0.75rem', margin: '4px 0 0 0' };
const statusBadge = (ready) => ({ color: ready ? '#2ecc71' : '#ff4d4d', fontSize: '0.6rem', border: `1px solid ${ready ? '#2ecc7133' : '#ff4d4d33'}`, padding: '4px 8px', borderRadius: '4px', fontWeight: 'bold' });
const controlGroup = { display: 'flex', gap: '10px', marginTop: '15px' };
const selectStyle = { padding: '12px', flex: 1, borderRadius: '8px', background: '#111', color: 'white', border: '1px solid #333', fontSize: '0.85rem' };
const btnStyle = (disabled) => ({ backgroundColor: disabled ? '#222' : GOLD, color: 'black', padding: '10px 20px', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: disabled ? 'not-allowed' : 'pointer', fontSize: '0.85rem' });
const resultContainer = { marginTop: '20px', display: 'flex', flexDirection: 'column', gap: '10px' };
const imgStyle = { width: '100%', borderRadius: '8px', border: `1px solid ${GOLD}` };
const shareBtn = { background: 'transparent', color: GOLD, border: `1px solid ${GOLD}`, padding: '10px', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', fontSize: '0.8rem' };

export default AiRender;
